/**
 * What the journal does when the server says "that changed under you".
 *
 * Nothing here merges text. A body conflict keeps both versions: the rejected
 * text becomes a new "(copy)" entry the open editor switches to, and
 * the original reloads from the server. A frontmatter conflict retries once
 * without a revision (title and tags are small, last writer wins). A path
 * collision on a create moves the create to the path the server suggested.
 * Every handler runs outside the framework's locks; each checks the write's
 * scope is still the signed-in store before touching anything.
 */

import { apiPost } from '@/shared/services/api';
import type { ApiError } from '@/shared/services/api';
import {
  broadcastInvalidated,
  deviceId,
  invalidateResource,
  notifyEntityMoved,
  scopeIsCurrent,
  sendRaw,
} from '@shared/offline';

import { entryKey, idFromUrl, journalEntity, titleBlock } from './journalContent';
import { getJournalFileById } from './journalFileApi';

import type { NotesFileContent } from '@features/notes/services/types';
import type { ConflictOutcome, WriteRequest, WriteScope } from '@shared/offline';

const COPY_SUFFIX = ' (copy)';
const CREATE_URL = '/api/journal/files';

/** What a conflict copy was made of, for the page to say so. */
export interface CopyNotice {
  originalId: string;
  copyId: string;
  title: string;
}

type CopyListener = (notice: CopyNotice) => void;
const copyListeners = new Set<CopyListener>();

/** Be told when a conflict produced a copy, so the page can say so. Returns a disposer. */
export function onCopyCreated(listener: CopyListener): () => void {
  copyListeners.add(listener);
  return () => {
    copyListeners.delete(listener);
  };
}

/** The conflict details the server attached, if any. */
function detailsOf(err: ApiError): Record<string, unknown> {
  const body = err.body as { details?: Record<string, unknown> } | undefined;
  return body?.details ?? {};
}

/**
 * A stable id for the copy of `originalId` holding `text`, rejected at
 * `heldRevision` on this device: SHA-256 of all four, shaped as a v4 UUID so
 * the server accepts it. Same text on a retry finds the copy already created
 * (the create is idempotent by id); different text, or another device, makes
 * a different copy, so no copy is ever overwritten.
 */
export async function copyIdFor(
  originalId: string,
  heldRevision: string | null,
  device: string,
  text: string
): Promise<string> {
  const bytes = new TextEncoder().encode(`${originalId}:${heldRevision ?? ''}:${device}:${text}`);
  const digest = new Uint8Array(await crypto.subtle.digest('SHA-256', bytes));
  digest[6] = (digest[6]! & 0x0f) | 0x40;
  digest[8] = (digest[8]! & 0x3f) | 0x80;
  const hex = Array.from(digest.slice(0, 16), b => b.toString(16).padStart(2, '0')).join('');
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

/**
 * What the copy is called and where it lives, from the cached original when
 * there is one. With a queued row the read serves the cache; without one it
 * is network-first and names the copy after the server's version. Either is
 * right: only the text must come from the request.
 */
async function copyIdentity(
  originalId: string
): Promise<{ title: string; path: string; tags: string[] }> {
  const cached = await getJournalFileById(originalId).catch(() => null);
  const basePath = cached?.path ?? `${originalId}.md`;
  const rawTags = cached?.frontmatter?.tags;
  return {
    title: `${baseTitle(cached, originalId)}${COPY_SUFFIX}`,
    path: `${basePath.replace(/\.md$/, '')}${COPY_SUFFIX}.md`,
    tags: Array.isArray(rawTags) ? rawTags.map(t => String(t)) : [],
  };
}

/** The original's title: the entry's, else its frontmatter's, else the id. */
function baseTitle(cached: NotesFileContent | null, originalId: string): string {
  if (cached?.title) return cached.title;
  const fmTitle = cached?.frontmatter?.title;
  return typeof fmTitle === 'string' && fmTitle ? fmTitle : originalId;
}

/**
 * Body conflict: keep the rejected text as a copy, reload the original, switch the editor.
 *
 * The create is idempotent by an id derived from the text, so a retry finds
 * the copy it already made and never overwrites one. The text is the
 * request's, never the cached entry's: the cache may already hold a later
 * echo, and the cached body is exactly what the server refused to accept.
 * Resolves `resolved-announced`: the original's key is announced as
 * discarded here, in every tab, and the framework must not then call the
 * same key acknowledged.
 */
export async function offlineCopy(
  req: WriteRequest,
  _err: ApiError,
  scope: WriteScope
): Promise<ConflictOutcome> {
  if (!scopeIsCurrent(scope)) return 'failed';
  const originalId = idFromUrl(req.url);
  const { content: text, expected_revision: held = null } = req.body as {
    content: string;
    expected_revision?: string | null;
  };
  try {
    const copyId = await copyIdFor(originalId, held, await deviceId(), text);
    const { title, path, tags } = await copyIdentity(originalId);
    // The lookups above awaited; a sign-out or account switch meanwhile must
    // not send this account's text through the next account's session.
    if (!scopeIsCurrent(scope)) return 'failed';
    await apiPost(CREATE_URL, { id: copyId, path, content: `${titleBlock(title, tags)}${text}` });
    // And once more after the network: a store wiped or switched meanwhile
    // must not have an editor moved or a key invalidated on its behalf.
    if (!scopeIsCurrent(scope)) return 'failed';
    notifyEntityMoved({
      from: journalEntity(originalId),
      to: journalEntity(copyId),
      detail: { path },
    });
    copyListeners.forEach(listener => listener({ originalId, copyId, title }));
    // The original's cached copy held the rejected text; drop it so the next
    // read brings the server's version. "Discarded", not "acknowledged": a
    // listener still on the original must reload it, never mark a save synced.
    const key = entryKey(originalId);
    await invalidateResource(key);
    broadcastInvalidated([key], 'discarded');
    return 'resolved-announced';
  } catch {
    return 'failed';
  }
}

/** Frontmatter conflict: last writer wins, once, without a revision. */
export async function retryWithoutRevision(
  req: WriteRequest,
  _err: ApiError,
  scope: WriteScope
): Promise<ConflictOutcome> {
  if (!scopeIsCurrent(scope)) return 'failed';
  const { expected_revision: _dropped, ...rest } = req.body as Record<string, unknown>;
  try {
    await sendRaw(req.method, req.url, rest);
    return 'resolved';
  } catch {
    return 'failed';
  }
}

/**
 * Create-path collision: take the path the server would have chosen.
 *
 * Sent raw, not through the write seam: rows queued behind this create lose
 * their dependency the moment it is resolved, so the replacement must be on
 * the server before then, never queued behind them. A 409 with no suggested
 * path is the id being unavailable, which nothing here can fix.
 */
export async function moveToSuggestedPath(
  req: WriteRequest,
  err: ApiError,
  scope: WriteScope
): Promise<ConflictOutcome> {
  if (!scopeIsCurrent(scope)) return 'failed';
  const suggested = detailsOf(err).suggested_path;
  if (typeof suggested !== 'string') return 'failed';
  const body = req.body as { id: string; path: string; content: string };
  try {
    await sendRaw('POST', CREATE_URL, { ...body, path: suggested });
    return 'resolved';
  } catch {
    return 'failed';
  }
}
